/**
 * Organization files (Business Account Pages slice 2, migration 2026-10-06-314).
 *
 * Metadata lives in public.documents; bytes live in the PRIVATE bucket `organization-files` at
 * `<organization_id>/<document id>/<file name>`. Access is internal staff only (table RLS + bucket-scoped storage
 * policies). The bucket refuses any object whose path was not registered first by the same user, so:
 *   upload   = check the file's first bytes match its type (client pre-check) -> insert the documents row -> upload
 *              the object (no overwrite; on failure delete the row) -> verify-organization-file checks the STORED
 *              bytes on the server and sets verified_at, or deletes the object and the row (audit S2-2, migration 318)
 *   read     = only verified files can be signed/downloaded (storage policy requires documents.verified_at)
 *   download = short-lived signed URL (60 s)
 *   delete   = remove the object -> delete the row
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import { sniffFile, sniffLength } from "../utils/fileSniff";

export const ORGANIZATION_FILES_BUCKET = "organization-files";

export const DOCUMENT_CATEGORIES = [
  { value: "agreement", label: "Agreement" },
  { value: "vendor", label: "Vendor" },
  { value: "other", label: "Other" },
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number]["value"];

export const documentCategoryLabel = (c?: string | null): string =>
  DOCUMENT_CATEGORIES.find((x) => x.value === c)?.label ?? "Other";

/** Mirrors the bucket's allowed_mime_types and file_size_limit. */
export const ALLOWED_FILE_TYPES: Record<string, string> = {
  "application/pdf": "PDF",
  "image/png": "PNG",
  "image/jpeg": "JPEG",
  "image/gif": "GIF",
  "image/webp": "WebP",
  "application/msword": "Word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "Word",
  "application/vnd.ms-excel": "Excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.ms-powerpoint": "PowerPoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PowerPoint",
  "text/csv": "CSV",
  "text/plain": "Text",
};
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const FILE_ACCEPT = Object.keys(ALLOWED_FILE_TYPES).join(",");

export interface OrganizationDocument {
  id: UUID;
  organization_id: UUID;
  name: string;
  file_type: string;
  file_size_bytes: number;
  storage_path: string;
  category: DocumentCategory;
  uploaded_by: UUID | null;
  uploaded_at: string;
  /** set by the server once the stored bytes matched file_type; NULL = not readable yet */
  verified_at: string | null;
  uploader?: { full_name: string | null; email: string | null } | null;
}

/** Returns an operator message when the file cannot be uploaded, else null. */
export const checkUploadable = (file: File): string | null => {
  if (!ALLOWED_FILE_TYPES[file.type]) {
    return `“${file.name}” is not an allowed file type. Allowed: PDF, images (PNG, JPEG, GIF, WebP), Word, Excel, PowerPoint, CSV and text.`;
  }
  if (file.size > MAX_FILE_BYTES) {
    return `“${file.name}” is larger than 25 MB.`;
  }
  if (file.size === 0) {
    return `“${file.name}” is empty.`;
  }
  return null;
};

/** Storage-safe object name: keep letters, digits, dot, dash, underscore; collapse the rest. */
export const safeObjectName = (name: string): string => {
  const cleaned = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w.-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^[._]+/, "")
    .slice(-120);
  return cleaned || "file";
};

export const getOrganizationDocuments = async (
  organizationId: UUID,
): Promise<OrganizationDocument[]> => {
  const { data, error } = await supabase
    .from("documents")
    .select(
      "id, organization_id, name, file_type, file_size_bytes, storage_path, category, uploaded_by, uploaded_at, verified_at, uploader:profiles!documents_uploaded_by_fkey(full_name, email)",
    )
    .eq("organization_id", organizationId)
    .order("uploaded_at", { ascending: false })
    .limit(500);
  if (error) {
    throw error;
  }
  return (data ?? []) as unknown as OrganizationDocument[];
};

export const countOrganizationDocuments = async (
  organizationId: UUID,
): Promise<number> => {
  const { count, error } = await supabase
    .from("documents")
    .select("id", { count: "exact", head: true })
    .eq("organization_id", organizationId);
  if (error) {
    throw error;
  }
  return count ?? 0;
};

/** Client pre-check of the first bytes against the declared type (the server re-checks the stored bytes). */
const readBytes = (blob: Blob): Promise<ArrayBuffer> =>
  typeof blob.arrayBuffer === "function"
    ? blob.arrayBuffer()
    : new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as ArrayBuffer);
        r.onerror = () => reject(r.error);
        r.readAsArrayBuffer(blob);
      });

export const checkFileContent = async (file: File): Promise<string | null> => {
  const head = new Uint8Array(
    await readBytes(file.slice(0, sniffLength(file.type))),
  );
  const verdict = sniffFile(head, file.type);
  return verdict.ok
    ? null
    : `“${file.name}” does not look like a ${ALLOWED_FILE_TYPES[file.type] ?? file.type} file (${verdict.reason}).`;
};

export class FileContentMismatchError extends Error {}

/** Server-side content verification. Throws FileContentMismatchError when the server deleted the file. */
export const verifyOrganizationDocument = async (
  documentId: UUID,
): Promise<void> => {
  const { data, error } = await supabase.functions.invoke(
    "verify-organization-file",
    {
      body: { document_id: documentId },
    },
  );
  if (error) {
    const ctx = (error as { context?: Response }).context;
    let body: { error?: string; reason?: string; detail?: string } | null =
      null;
    try {
      body = ctx ? await ctx.json() : null;
    } catch {
      body = null;
    }
    if (body?.error === "FILE_CONTENT_MISMATCH") {
      throw new FileContentMismatchError(
        `The file's content does not match its type (${body.reason ?? "mismatch"}); it was removed.`,
      );
    }
    // OBJECT_CHANGED: the file was replaced while it was being checked; OBJECT_MISSING: nothing stored at the path
    throw new Error(
      body?.detail ||
        body?.error ||
        error.message ||
        "The file could not be checked.",
    );
  }
  if (!(data as { verified?: boolean } | null)?.verified) {
    throw new Error("The file could not be checked.");
  }
};

export const uploadOrganizationDocument = async (
  organizationId: UUID,
  file: File,
  category: DocumentCategory,
  displayName?: string,
): Promise<OrganizationDocument> => {
  const problem = checkUploadable(file) ?? (await checkFileContent(file));
  if (problem) {
    throw new Error(problem);
  }
  const id = crypto.randomUUID();
  const path = `${organizationId}/${id}/${safeObjectName(file.name)}`;
  const { data: row, error: rowError } = await supabase
    .from("documents")
    .insert({
      id,
      organization_id: organizationId,
      name: (displayName || file.name).trim().slice(0, 255),
      file_type: file.type,
      file_size_bytes: file.size,
      storage_path: path,
      category,
    })
    .select()
    .single();
  if (rowError) {
    throw rowError;
  }
  const { error: upError } = await supabase.storage
    .from(ORGANIZATION_FILES_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upError) {
    // undo the registration so no row points at a missing object
    await supabase.from("documents").delete().eq("id", id);
    throw upError;
  }
  // server-side content check; a mismatch is deleted by the server and surfaces as FileContentMismatchError
  await verifyOrganizationDocument(id);
  return row as OrganizationDocument;
};

export const getDocumentSignedUrl = async (
  doc: Pick<OrganizationDocument, "storage_path" | "name">,
  download: boolean,
): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(ORGANIZATION_FILES_BUCKET)
    .createSignedUrl(
      doc.storage_path,
      60,
      download ? { download: doc.name } : undefined,
    );
  if (error || !data?.signedUrl) {
    throw error || new Error("The file is not available.");
  }
  return data.signedUrl;
};

export const updateOrganizationDocument = async (
  id: UUID,
  updates: { name?: string; category?: DocumentCategory },
): Promise<void> => {
  const { error } = await supabase
    .from("documents")
    .update(updates)
    .eq("id", id);
  if (error) {
    throw error;
  }
};

export const deleteOrganizationDocument = async (
  doc: Pick<OrganizationDocument, "id" | "storage_path">,
): Promise<void> => {
  const { error: objError } = await supabase.storage
    .from(ORGANIZATION_FILES_BUCKET)
    .remove([doc.storage_path]);
  if (objError) {
    throw objError;
  }
  const { error } = await supabase.from("documents").delete().eq("id", doc.id);
  if (error) {
    throw error;
  }
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
